import React, { useState } from 'react'
import GlassPanel from '../UI/GlassPanel'
import LoadingSpinner from '../UI/LoadingSpinner'
import SearchResultThumb from './SearchResultThumb'
import { clickableProps } from '../../utils/clickableProps'

const EventSearchSection = ({
  searchQuery,
  onSearchQueryChange,
  onSearch,
  searching,
  searchResults,
  ticketmasterResults,
  selectedFlyer,
  flyerPreview,
  onFlyerUpload,
  onRemoveFlyerUpload,
  onEventSelect,
  onTicketmasterEventSelect,
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  includeTicketmaster = true,
  onIncludeTicketmasterChange
}) => {
  const [searchMode, setSearchMode] = useState('text') // 'text' or 'flyer'
  const [activeTab, setActiveTab] = useState('local') // 'local' or 'ticketmaster'

  const hasLocalResults = searchResults.length > 0
  const hasTicketmasterResults = ticketmasterResults.length > 0
  const hasAnyResults = hasLocalResults || hasTicketmasterResults

  // Auto-switch to ticketmaster tab if only ticketmaster results
  React.useEffect(() => {
    if (hasTicketmasterResults && !hasLocalResults) {
      setActiveTab('ticketmaster')
    } else if (hasLocalResults && !hasTicketmasterResults) {
      setActiveTab('local')
    }
  }, [hasLocalResults, hasTicketmasterResults])

  const handleModeSwitch = (mode) => {
    setSearchMode(mode)
    if (mode === 'text' && selectedFlyer) {
      onRemoveFlyerUpload()
    }
    if (mode === 'flyer' && searchQuery) {
      onSearchQueryChange('')
    }
  }

  return (
    <GlassPanel className="p-6 mb-8">
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2" style={{color: 'var(--theme-text-primary)'}}>🔍 Find Existing Event</h2>
        <p className="text-sm" style={{color: 'var(--theme-text-secondary)'}}>Search for events or upload a flyer to auto-fill details</p>
      </div>

      {/* Search Mode Toggle */}
      <div className="flex gap-2 mb-4">
        <button
          type="button"
          onClick={() => handleModeSwitch('text')}
          className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
            searchMode === 'text'
              ? 'bg-purple-600 text-white shadow-lg'
              : 'bg-white/10 text-gray-300 hover:bg-white/20'
          }`}
        >
          🔍 Text Search
        </button>
        <button
          type="button"
          onClick={() => handleModeSwitch('flyer')}
          className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
            searchMode === 'flyer'
              ? 'bg-purple-600 text-white shadow-lg'
              : 'bg-white/10 text-gray-300 hover:bg-white/20'
          }`}
        >
          📄 Flyer Upload
        </button>
      </div>

      {/* Search Input */}
      {searchMode === 'text' && (
        <div className="mb-6">
          <div className="flex gap-3">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return
                // This input sits inside the <form> that creates the event.
                // Without preventDefault, Enter triggers implicit form
                // submission: the browser runs required-field validation and
                // reports an error against Basic Event Information, or — worse,
                // once those fields are filled — actually submits the event.
                e.preventDefault()
                onSearch()
              }}
              placeholder="Search events (local + Ticketmaster)..."
              className="flex-1 px-4 py-3 bg-white/10 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
              style={{color: 'var(--theme-text-primary)'}}
            />
            <button
              type="button"
              onClick={onSearch}
              disabled={searching || !searchQuery.trim()}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-xl transition-all duration-200 min-w-[100px]"
            >
              {searching ? <LoadingSpinner text="Searching…" /> : 'Search'}
            </button>
          </div>

          {/* Turning this off skips the Ticketmaster request entirely rather
              than hiding its results, which keeps the third-party quota for
              searches that actually want it. */}
          <label className="mt-3 inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeTicketmaster}
              onChange={(e) => onIncludeTicketmasterChange?.(e.target.checked)}
              className="w-4 h-4 rounded accent-blue-500"
            />
            <span className="text-sm" style={{color: 'var(--theme-text-secondary)'}}>
              🎫 Also search Ticketmaster
            </span>
          </label>
        </div>
      )}

      {/* Flyer Upload */}
      {searchMode === 'flyer' && (
        <div className="mb-6">
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 ${
              isDragOver
                ? 'border-purple-400 bg-purple-400/10'
                : 'border-white/30 hover:border-white/50'
            }`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            {selectedFlyer ? (
              <div className="space-y-4">
                <div className="text-green-500 font-medium">✓ Flyer uploaded!</div>
                {flyerPreview && (
                  <img
                    src={flyerPreview}
                    alt="Event flyer"
                    className="max-w-xs mx-auto rounded-lg shadow-lg"
                  />
                )}
                <div className="flex gap-3 justify-center">
                  <button
                    type="button"
                    onClick={onSearch}
                    disabled={searching}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200"
                  >
                    {searching ? <LoadingSpinner size="sm" text="Processing..." /> : '🎯 Extract Details'}
                  </button>
                  <button
                    type="button"
                    onClick={onRemoveFlyerUpload}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200"
                  >
                    🗑️ Remove
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="text-4xl mb-4">📄</div>
                <p className="font-medium mb-2" style={{color: 'var(--theme-text-primary)'}}>Drop flyer here or click to browse</p>
                <p className="text-sm mb-4" style={{color: 'var(--theme-text-secondary)'}}>Supports JPG, PNG, PDF files</p>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={onFlyerUpload}
                  className="hidden"
                  id="flyerUpload"
                />
                <label
                  htmlFor="flyerUpload"
                  className="inline-block bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-4 rounded-lg border border-white/20 hover:border-white/40 transition-all duration-200 cursor-pointer"
                >
                  📁 Browse Files
                </label>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search Results */}
      {hasAnyResults && (
        <div className="space-y-4">
          {/* Results Tabs */}
          {hasLocalResults && hasTicketmasterResults && (
            <div className="flex gap-2 border-b border-white/20">
              <button
                type="button"
                onClick={() => setActiveTab('local')}
                className={`px-4 py-2 font-medium transition-all duration-200 ${
                  activeTab === 'local'
                    ? 'text-purple-400 border-b-2 border-purple-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                📚 Your Events ({searchResults.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('ticketmaster')}
                className={`px-4 py-2 font-medium transition-all duration-200 ${
                  activeTab === 'ticketmaster'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                🎫 Ticketmaster ({ticketmasterResults.length})
              </button>
            </div>
          )}

          {/* Results Content */}
          <div className="max-h-80 overflow-y-auto space-y-2 min-h-[4rem]">
            {/* Local Results */}
            {(activeTab === 'local' || !hasTicketmasterResults) && hasLocalResults && (
              <>
                {hasLocalResults && hasTicketmasterResults && (
                  <h3 className="text-white font-medium mb-3 flex items-center">
                    📚 Your Events ({searchResults.length})
                  </h3>
                )}
                {searchResults.map((result, index) => (
                  <div
                    key={`local-${index}`}
                    {...clickableProps(() => onEventSelect(result), `Use event ${result.name}`)}
                    className="bg-white/5 hover:bg-white/10 rounded-lg p-4 border border-white/10 cursor-pointer transition-all duration-200 group focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
                  >
                    <div className="flex items-center gap-3">
                      <SearchResultThumb event={result} />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium group-hover:text-purple-500 transition-colors truncate" style={{color: 'var(--theme-text-primary)'}}>
                          {result.name}
                        </h4>
                        <p className="text-sm truncate" style={{color: 'var(--theme-text-secondary)'}}>
                          {result.venueName}{result.country && `, ${result.country}`}
                        </p>
                        <p className="text-xs" style={{color: 'var(--theme-text-tertiary)'}}>
                          {result.startDate && new Date(result.startDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-purple-400 group-hover:text-purple-300 font-medium transition-colors flex-shrink-0">
                        Use →
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Ticketmaster Results */}
            {(activeTab === 'ticketmaster' || !hasLocalResults) && hasTicketmasterResults && (
              <>
                {hasLocalResults && hasTicketmasterResults && (
                  <h3 className="text-white font-medium mb-3 flex items-center">
                    🎫 Ticketmaster Events ({ticketmasterResults.length})
                  </h3>
                )}
                {ticketmasterResults.map((result, index) => (
                  <div
                    key={`tm-${index}`}
                    {...clickableProps(
                      () => onTicketmasterEventSelect(result),
                      `Import event ${result.name} from Ticketmaster`
                    )}
                    className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 hover:from-blue-500/20 hover:to-purple-500/20 rounded-lg p-4 border border-blue-400/20 cursor-pointer transition-all duration-200 group focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                  >
                    <div className="flex items-center gap-3">
                      <SearchResultThumb event={result} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium group-hover:text-blue-500 transition-colors truncate" style={{color: 'var(--theme-text-primary)'}}>
                            {result.name}
                          </h4>
                          <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full">
                            Ticketmaster
                          </span>
                        </div>
                        <p className="text-sm" style={{color: 'var(--theme-text-secondary)'}}>
                          {result.venue?.name}{result.venue?.city && `, ${result.venue.city}`}
                        </p>
                        <p className="text-xs" style={{color: 'var(--theme-text-tertiary)'}}>
                          {result.date && new Date(result.date).toLocaleDateString()}
                          {result.time && ` at ${result.time}`}
                        </p>
                        {result.artists && result.artists.length > 0 && (
                          <p className="text-purple-300 text-xs mt-1 truncate">
                            Artists: {result.artists.map(a => a.name).slice(0, 3).join(' ')}
                            {result.artists.length > 3 && ` +${result.artists.length - 3} more`}
                          </p>
                        )}
                        {result.priceRange && (
                          <p className="text-green-300 text-xs">
                            ${result.priceRange.min} - ${result.priceRange.max} {result.priceRange.currency}
                          </p>
                        )}
                      </div>
                      <div className="text-blue-400 group-hover:text-blue-300 font-medium transition-colors flex-shrink-0">
                        Import →
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* No Results Message */}
      {searchQuery && !searching && !hasAnyResults && (
        <div className="text-center py-8" style={{color: 'var(--theme-text-secondary)'}}>
          <div className="text-4xl mb-2">🔍</div>
          <p>No events found for "{searchQuery}"</p>
          <p className="text-sm mt-1">Try a different search term or check spelling</p>
        </div>
      )}
    </GlassPanel>
  )
}

export default EventSearchSection